import { PremiumGem } from '@features/premium/components/PremiumGem.js';
import { useBecomePremium } from '@features/premium/hooks/useBecomePremium.js';
import { ExperimentalFunction } from '@shared/components/ExperimentalFunction.js';
import { FmDropdownMenu } from '@shared/components/FmDropdownMenu.js';
import { LongPressTooltip } from '@shared/components/LongPressTooltip.js';
import { MenuGutter } from '@shared/components/MenuGutter.js';
import {
  type TransportType,
  transportTypeDefs,
} from '@shared/transportTypeDefs.js';
import { Fragment, type ReactElement } from 'react';
import { Dropdown } from 'react-bootstrap';
import { FaEquals, FaMoneyBill } from 'react-icons/fa';
import { useRoutePlannerMessages } from '../translations/useRoutePlannerMessages.js';

type Props = {
  onChange: (value?: TransportType) => void;
  value?: TransportType;
  withDefault?: boolean;
};

export function RoutePlannerTransportType({
  onChange,
  value,
  withDefault = false,
}: Props): ReactElement {
  const rpm = useRoutePlannerMessages();

  const activeTTDef = value && transportTypeDefs[value];

  const ttLabel = activeTTDef
    ? rpm?.transportType[activeTTDef.msgKey]
    : rpm?.default;

  const becomePremium = useBecomePremium();

  return (
    <Dropdown
      id="transport-type"
      onSelect={(transportType) =>
        onChange((transportType || undefined) as TransportType)
      }
    >
      <LongPressTooltip
        breakpoint="md"
        name={rpm?.transportTypeLabel}
        label={
          activeTTDef ? (
            <>
              {ttLabel ?? '…'}

              {activeTTDef.api !== 'manual' && (
                <>
                  {' '}
                  <small className="text-dark">
                    {activeTTDef.api === 'osrm' ? 'OSRM' : 'GraphHopper'}
                  </small>
                </>
              )}
            </>
          ) : (
            (ttLabel ?? '…')
          )
        }
      >
        {({ label, labelClassName, props }) => (
          <Dropdown.Toggle variant="secondary" {...props}>
            {!value ? <FaEquals /> : (activeTTDef?.icon ?? '')}{' '}
            {value && ['car', 'car-toll', 'bikesharing'].includes(value) && (
              <FaMoneyBill />
            )}
            <span className={labelClassName}> {label}</span>
          </Dropdown.Toggle>
        )}
      </LongPressTooltip>

      <FmDropdownMenu>
        {withDefault && (
          <Dropdown.Item as="button" eventKey="" active={!value}>
            <FaEquals /> {rpm?.default}
          </Dropdown.Item>
        )}

        {(['manual', 'gh', 'osrm'] as const).map((api) => (
          <Fragment key={api}>
            {api !== 'manual' && (
              <Dropdown.Header>
                {api === 'osrm' ? 'OSRM' : 'GraphHopper '}
              </Dropdown.Header>
            )}

            {Object.entries(transportTypeDefs)
              .filter(([, def]) => !def.hidden && def.api === api)
              .map(([type, { icon, msgKey: key, experimental }]) => (
                <Dropdown.Item
                  as="button"
                  eventKey={type}
                  key={type}
                  title={rpm?.transportType[key]}
                  active={value === type}
                  disabled={withDefault && Boolean(becomePremium)}
                >
                  {icon}{' '}
                  {['car', 'car-toll', 'bikesharing'].includes(type) && (
                    <FaMoneyBill />
                  )}{' '}
                  {rpm?.transportType[key] ?? '…'}
                  {(experimental || withDefault) && (
                    <MenuGutter>
                      {experimental && <ExperimentalFunction />}
                      {withDefault && <PremiumGem nested />}
                    </MenuGutter>
                  )}
                </Dropdown.Item>
              ))}
          </Fragment>
        ))}
      </FmDropdownMenu>
    </Dropdown>
  );
}
